import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      <div className="mb-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>
      </div>

      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      <div className="text-sm text-gray-600 mb-8">
        Effective Date: April 29, 2026<br />
        Last Updated: April 29, 2026
      </div>

      <div className="prose prose-lg max-w-none">
        <p>
          GSL Solutions, Inc. ("GSL Solutions," "we," "us," or "our") operates Fuel Central, an automated valuation platform for retail
          gasoline service stations and related properties (the "Services"). This Privacy Policy describes the personal information we
          collect, how we use and share it, and the rights and choices you have regarding that information.
        </p>

        <p>
          This Privacy Policy applies to information we collect through our website at www.thefuelcentral.com and any related services,
          communications, or applications that link to this Privacy Policy.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">1. Information We Collect</h2>

        <h3 className="text-lg font-semibold mt-6 mb-3">(a) Information You Provide Directly</h3>
        <p>
          <strong>Account information.</strong> When you register for an account, we collect your name, email address, and a password
          (which we store in hashed form, not in plain text). We may also collect optional profile details you choose to provide.
        </p>
        <p>
          <strong>Payment information.</strong> When you purchase a valuation or subscribe to a paid plan, our payment processor (Stripe)
          collects your payment card details and billing information directly. <strong>We do not receive, store, or have access to your
          full payment card number.</strong> We receive only limited transaction information from Stripe — such as the last four digits
          of the card, the card brand, the transaction amount, and a transaction identifier — sufficient for us to confirm the payment
          and provide customer support.
        </p>
        <p>
          <strong>Property and valuation inputs.</strong> When you generate a valuation, you submit information about the subject
          property, which may include the property address, dispenser counts, fuel volumes, brand, lease and operating data, and other
          commercially relevant details. We refer to this as your "Property Submission Data." Property Submission Data may include
          commercially sensitive information about properties you own, operate, or are evaluating for acquisition.
        </p>
        <p>
          <strong>Communications.</strong> When you contact us by email, web form, or phone, we collect your contact details and the
          contents of your message.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(b) Information Collected Automatically</h3>
        <p>
          <strong>Usage data.</strong> When you visit or use the Services, we automatically collect information about your interaction,
          including pages viewed, features used, valuations generated, time spent on the site, and clickstream activity.
        </p>
        <p>
          <strong>Device and log data.</strong> We collect IP address, browser type and version, operating system, device identifiers,
          referring URL, and date/time stamps.
        </p>
        <p>
          <strong>Cookies and similar technologies.</strong> We use cookies, web beacons, and similar tracking technologies. See
          Section 4 for details.
        </p>

        <h3 className="text-lg font-semibold mt-6 mb-3">(c) Information from Third Parties</h3>
        <p>We may receive information about you from:</p>
        <ul className="list-disc pl-6 mb-4">
          <li><strong>Stripe</strong>, our payment processor — payment status, transaction metadata, and dispute information.</li>
          <li><strong>Google Analytics</strong> — aggregated usage analytics about visitors to our site.</li>
          <li><strong>Authentication providers</strong> (if you sign in via a third-party account) — your name, email, and a unique identifier.</li>
          <li><strong>Public and licensed data sources</strong> — we ingest third-party real estate, transaction, demographic, and traffic data to power our valuation models. This data generally does not relate to you personally.</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">2. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Provide, operate, and maintain the Services;</li>
          <li>Generate the valuation estimates you request;</li>
          <li>Process payments and manage subscriptions;</li>
          <li>Authenticate users and secure accounts;</li>
          <li>Communicate with you about your account, transactions, and support requests;</li>
          <li>Send service-related and (with your consent) marketing communications, including our newsletter;</li>
          <li>Detect, prevent, and respond to fraud, abuse, security incidents, and violations of our Terms of Service;</li>
          <li>Comply with legal obligations and enforce our agreements;</li>
          <li>Analyze usage patterns to improve the Services;</li>
          <li>
            <strong>Improve our valuation models.</strong> We may use Property Submission Data, in aggregated and de-identified form,
            to train, validate, refine, and benchmark our automated valuation models. We do not share individual, identifiable Property
            Submission Data with third parties for model training, advertising, or commercial resale.
          </li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-4">3. How We Share Your Information</h2>
        <p>We share personal information only as described below.</p>
        <p>
          <strong>Service providers (subprocessors).</strong> We share information with vendors who help us operate the Services. These
          providers are contractually limited to using your information only to provide services to us. Our current subprocessors include:
        </p>
        <ul className="list-disc pl-6 mb-4">
          <li><strong>Stripe</strong> — payment processing</li>
          <li><strong>Supabase</strong> — database, authentication, and backend infrastructure</li>
          <li><strong>Fly.io</strong> — application hosting and edge compute</li>
          <li><strong>Google Analytics</strong> — usage analytics</li>
          <li><strong>Email service providers</strong> — transactional and marketing email delivery</li>
        </ul>
        <p>We update this list from time to time as our infrastructure evolves.</p>
        <p>
          <strong>Business transfers.</strong> If we are involved in a merger, acquisition, financing, reorganization, or sale of assets,
          your information may be transferred as part of that transaction, subject to standard confidentiality protections.
        </p>
        <p>
          <strong>Legal compliance and protection.</strong> We may disclose information when we believe in good faith that disclosure is
          necessary to (i) comply with applicable law, regulation, legal process, or governmental request; (ii) enforce our Terms of
          Service; (iii) detect, prevent, or address fraud, security, or technical issues; or (iv) protect the rights, property, or
          safety of GSL Solutions, our users, or the public.
        </p>
        <p>
          <strong>With your consent.</strong> We share information with third parties when you direct us to do so or otherwise consent.
        </p>
        <p>
          <strong>We do not sell your personal information</strong> and we do not "share" your personal information for cross-context
          behavioral advertising as those terms are defined under the California Consumer Privacy Act (CCPA/CPRA).
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">4. Cookies and Tracking Technologies</h2>
        <p>We use cookies and similar technologies to:</p>
        <ul className="list-disc pl-6 mb-4">
          <li>Keep you signed in and remember your preferences;</li>
          <li>Understand how visitors use the Services (via Google Analytics);</li>
          <li>Maintain security and prevent fraud.</li>
        </ul>
        <p>
          You can control cookies through your browser settings. Most browsers allow you to refuse, accept, or delete cookies. Note that
          disabling cookies may affect the functionality of the Services, including your ability to remain signed in.
        </p>
        <p>
          You can opt out of Google Analytics by installing the{' '}
          <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
            Google Analytics Opt-out Browser Add-on
          </a>.
        </p>
        <p>
          We honor Global Privacy Control (GPC) signals where applicable as a "do not sell or share" request, although as noted above,
          we do not sell or share personal information for cross-context behavioral advertising.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">5. Data Retention</h2>
        <p>We retain personal information for as long as needed to provide the Services and for legitimate business purposes, including:</p>
        <ul className="list-disc pl-6 mb-4">
          <li><strong>Account information</strong> — for the lifetime of your account, plus a reasonable period after account closure for record-keeping, dispute resolution, and legal compliance.</li>
          <li><strong>Property Submission Data and generated valuations</strong> — retained in your account for as long as your account remains active, consistent with our Terms of Service.</li>
          <li><strong>Transaction and payment records</strong> — retained for at least seven (7) years to comply with tax, accounting, and audit obligations.</li>
          <li><strong>Communications and support records</strong> — retained for a reasonable period to support ongoing service relationships and resolve disputes.</li>
          <li><strong>Aggregated and de-identified data</strong> — may be retained indefinitely.</li>
        </ul>
        <p>When we no longer need personal information, we delete or de-identify it.</p>

        <h2 className="text-xl font-semibold mt-8 mb-4">6. Data Security</h2>
        <p>
          We implement reasonable administrative, technical, and physical safeguards designed to protect personal information from
          unauthorized access, disclosure, alteration, and destruction. These include encryption of data in transit, access controls on
          production systems, secure password hashing, and regular review of our security practices.
        </p>
        <p>
          However, no system is perfectly secure, and we cannot guarantee the absolute security of any information transmitted to or
          stored by us. You are responsible for maintaining the confidentiality of your account credentials.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">7. Your California Privacy Rights</h2>
        <p>
          If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the California Privacy Rights Act
          (CPRA), grants you the following rights regarding your personal information:
        </p>
        <p>
          <strong>Right to Know.</strong> You may request that we disclose the categories and specific pieces of personal information we
          have collected about you, the sources, the purposes for collection, and the categories of third parties with whom we have shared it.
        </p>
        <p>
          <strong>Right to Delete.</strong> You may request that we delete personal information we have collected from you, subject to
          certain exceptions (for example, information needed to complete a transaction, comply with legal obligations, or detect fraud).
        </p>
        <p>
          <strong>Right to Correct.</strong> You may request that we correct inaccurate personal information we maintain about you.
        </p>
        <p>
          <strong>Right to Opt Out of Sale or Sharing.</strong> As stated above, we do not sell or share personal information for
          cross-context behavioral advertising. You nonetheless have the right to direct us not to do so in the future.
        </p>
        <p>
          <strong>Right to Limit Use of Sensitive Personal Information.</strong> You have the right to limit our use of "sensitive
          personal information" as defined under California law to purposes necessary to provide the Services. We do not currently use
          sensitive personal information for any purpose beyond what is necessary to provide the Services.
        </p>
        <p>
          <strong>Right to Non-Discrimination.</strong> We will not discriminate against you for exercising any of these rights.
        </p>
        <p>
          <strong>How to Exercise Your Rights.</strong> To submit a request, email us at TheFuelCentral@gmail.com with the subject line
          "California Privacy Rights Request" and include sufficient information for us to verify your identity (such as the email
          address associated with your account). We will respond within the timeframes required by law (generally 45 days, with one
          possible 45-day extension).
        </p>
        <p>
          <strong>Authorized Agents.</strong> You may designate an authorized agent to make a request on your behalf. We will require
          written authorization and verification of the agent's identity.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">8. Children's Privacy</h2>
        <p>
          The Services are not directed to, and we do not knowingly collect personal information from, children under the age of 18.
          Our Terms of Service require all users to be at least 18 years old. If we become aware that we have collected personal
          information from a child under 18, we will delete it. If you believe a child has provided us with personal information,
          please contact us at the address below.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">9. International Users</h2>
        <p>
          The Services are operated from the United States and are intended for users located in the United States. If you access the
          Services from outside the United States, you understand that your information will be transferred to, stored, and processed
          in the United States, where data protection laws may differ from those in your jurisdiction.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">10. Third-Party Links</h2>
        <p>
          The Services may contain links to third-party websites or services. We are not responsible for the privacy practices or
          content of those third parties. We encourage you to review the privacy policies of any third-party sites you visit.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">11. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we make changes, we will update the "Last Updated" date above. If we
          make material changes, we will provide additional notice — for example, by email to the address associated with your account
          or by posting a prominent notice on the Services — at least thirty (30) days before the changes take effect. Your continued
          use of the Services after the effective date constitutes acceptance of the revised Privacy Policy.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-4">12. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy or our privacy practices, or to exercise your privacy rights, please contact us at:
        </p>
        <address className="not-italic mt-2 mb-4">
          GSL Solutions, Inc.<br />
          P.O. Box 3108<br />
          Corona, CA 92878<br />
          Email: TheFuelCentral@gmail.com<br />
          Phone: 951-223-5610
        </address>
      </div>

      <div className="my-8">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Home
        </Link>
      </div>
    </div>
  );
}
